// Popover — the anchored floating-panel primitive, pure. A thin wrapper
// over @radix-ui/react-popover.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// Where a Tooltip is a whispered, hover-gated aside, a Popover is a click-
// opened panel that holds RICHER content the user can move into and
// interact with (a form, a menu of actions, a detail card). It is a plane
// that genuinely floats above the page, so it wears the border→elevation
// discipline (§5): no resting edge, just the elevated ground + a soft
// shadow (--shadow-md, the popover level, same as Surface's `overlay`).
//
// Pure/props-driven: `trigger` is the anchor, rendered `asChild` so the
// caller's own focusable element (a Button, an IconButton…) BECOMES the
// trigger and keeps its semantics — the wrapper adds no second button and
// authors no visible text of its own (so, like Divider/Tooltip, no Lingui
// macro is needed here; every visible string is caller-supplied). `children`
// is the floating content. `side`/`align` tune placement as named unions
// (typed on the public surface, never a loose string). `open`/`onOpenChange`
// are genuine controlled/uncontrolled config — the panel reports every
// open/close request UP and the parent decides; no boolean flag swaps the
// component's meaning. All focus management (move focus in on open, restore
// on close), Escape/outside-click dismissal, and collision-aware positioning
// come from Radix — we only paint. No state, no I/O, no domain knowledge.

import * as RadixPopover from "@radix-ui/react-popover";
import type { ReactNode } from "react";
import "./Popover.css";

export type PopoverSide = "top" | "right" | "bottom" | "left";
export type PopoverAlign = "start" | "center" | "end";

export type PopoverProps = {
    // The anchor element. Rendered `asChild`: pass a single focusable node
    // (a Button, an IconButton…) and it becomes the popover's trigger.
    readonly trigger: ReactNode;
    // The panel's accessible name. Radix renders the content as role="dialog",
    // and a dialog without a name is an axe error (aria-dialog-name) — so the
    // name is REQUIRED, not optional: a short localised noun phrase naming
    // what the panel is ("Add link", "Evidence for this claim").
    readonly label: string;
    // The floating panel content — richer than a tooltip: forms, action
    // lists, detail cards. The caller owns every visible string inside it.
    readonly children: ReactNode;
    // Preferred edge of the trigger to open against (Radix flips it on
    // collision). Defaults to opening below the trigger.
    readonly side?: PopoverSide;
    // Alignment along that edge. Defaults to centered on the trigger.
    readonly align?: PopoverAlign;
    // Controlled: current open state. Pair with onOpenChange.
    readonly open?: boolean;
    // Reports every open/close request UP; the parent decides (pure core).
    readonly onOpenChange?: (open: boolean) => void;
    // The plane: padded with its arrow (default), or bare, for content that
    // draws its own plane (the Create menu).
    readonly look?: PopoverLook;
    // Escape is the host's: it steps back inside the panel and closes it
    // itself when there is nowhere to go back to (the Create menu's steps).
    // Absent: Escape closes (onOpenChange(false)).
    readonly onEscape?: () => void;
};

type PopoverLook = "panel" | "bare";

// The gap (px) between the trigger and the panel, sized to clear the arrow.
// A layout constant of the pointer geometry, not a themable surface value.
const SIDE_OFFSET = 6;

const Content = ({
    label,
    children,
    side = "bottom",
    align = "center",
    look = "panel",
    onEscape,
}: Omit<PopoverProps, "trigger" | "open" | "onOpenChange">) => (
    <RadixPopover.Portal>
        <RadixPopover.Content
            className={`bilda-popover bilda-popover--${look}`}
            aria-label={label}
            side={side}
            align={align}
            sideOffset={SIDE_OFFSET}
            onEscapeKeyDown={(event) => {
                if (onEscape === undefined) return;
                event.preventDefault();
                onEscape();
            }}
            // Closing gives focus back to the trigger, unless what the pick
            // opened has taken it already (Ask here's line, pen Q66).
            onCloseAutoFocus={(event) => {
                const at = document.activeElement;
                if (at !== null && at !== document.body) event.preventDefault();
            }}
        >
            {children}
            {look === "panel" ? (
                <RadixPopover.Arrow className="bilda-popover__arrow" />
            ) : null}
        </RadixPopover.Content>
    </RadixPopover.Portal>
);

export const Popover = ({
    trigger,
    open,
    onOpenChange,
    ...content
}: PopoverProps) => (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
        <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
        <Content {...content} />
    </RadixPopover.Root>
);

// A popover with no trigger: it sits at the PopoverAnchor rendered somewhere
// inside `around` (a place in the text, where no button is), and only its
// host opens it.
export const AnchoredPopover = ({
    around,
    open,
    onOpenChange,
    ...content
}: Omit<PopoverProps, "trigger"> & { readonly around: ReactNode }) => (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
        {around}
        <Content {...content} />
    </RadixPopover.Root>
);

// Where an AnchoredPopover above it in the tree sits.
export const PopoverAnchor = () => (
    <RadixPopover.Anchor className="bilda-popover__anchor" />
);
